import { useState } from 'react';
import { Link, useOutletContext, useSearchParams } from 'react-router-dom';
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, errMsg } from '../lib/api';
import { useDocumentTitle, useTid } from '../lib/hooks';
import { CLASSE_COR, classificar, fmtDayMon, plural } from '../lib/format';
import type { PeriodReport, TenantInfo, VisitSummary } from '../lib/types';
import { Button, ErrorState, Loading, NotaBadge, Segmented, Stat, Switch, VisitRow } from '../components/ui';
import { PdfButton } from '../components/PdfButton';
import { ShareSheet } from '../components/ShareSheet';
import { useToast } from '../components/feedback';

type Mode = 'visita' | 'semana' | 'mes';

export function Reports() {
  const tid = useTid();
  const info = useOutletContext<TenantInfo>();
  useDocumentTitle('Relatórios');
  const [params, setParams] = useSearchParams();
  const mode = (['visita', 'semana', 'mes'].includes(params.get('modo') ?? '') ? params.get('modo') : 'visita') as Mode;
  const inicio = params.get('inicio') ?? '';
  const setMode = (m: Mode) => setParams(m === 'visita' ? {} : { modo: m }, { replace: true });

  return (
    <div>
      <h1 className="h1" style={{ marginBottom: 14 }}>Relatórios</h1>
      <div style={{ maxWidth: 420, marginBottom: 16 }}>
        <Segmented<Mode> fill label="Tipo de relatório" value={mode} onChange={setMode} options={[['visita', 'Visita'], ['semana', 'Semana'], ['mes', 'Mês']]} />
      </div>
      {mode === 'visita' ? (
        <VisitList tid={tid} />
      ) : (
        <PeriodView tid={tid} kind={mode} inicio={inicio} canShareRole={info.can.share} onNavigate={(i) => setParams({ modo: mode, inicio: i }, { replace: true })} />
      )}
    </div>
  );
}

function VisitList({ tid }: { tid: string }) {
  const q = useInfiniteQuery({
    queryKey: ['visits', tid],
    initialPageParam: 0,
    queryFn: ({ pageParam }) => api.get<{ items: VisitSummary[]; hasMore: boolean }>(`/api/t/${tid}/visits?limit=40&offset=${pageParam}`),
    getNextPageParam: (last, all) => (last.hasMore ? all.reduce((n, p) => n + p.items.length, 0) : undefined),
  });
  if (q.error) return <ErrorState message={errMsg(q.error)} onRetry={() => q.refetch()} />;
  if (!q.data) return <Loading rows={4} />;
  const items = q.data.pages.flatMap((p) => p.items);
  if (!items.length) return <div className="empty">Nenhuma visita finalizada ainda. Os relatórios aparecem aqui ao finalizar cada visita.</div>;
  return (
    <>
      <div className="grid-list">
        {items.map((v) => <VisitRow key={v.id} v={v} to={`/t/${tid}/relatorio/${v.id}`} />)}
      </div>
      {q.hasNextPage && (
        <div style={{ textAlign: 'center', marginTop: 14 }}>
          <Button size="sm" loading={q.isFetchingNextPage} onClick={() => q.fetchNextPage()}>Carregar mais</Button>
        </div>
      )}
    </>
  );
}

function PeriodView({ tid, kind, inicio, canShareRole, onNavigate }: { tid: string; kind: 'semana' | 'mes'; inicio: string; canShareRole: boolean; onNavigate: (inicio: string) => void }) {
  const [sharing, setSharing] = useState(false);
  const q = useQuery({
    queryKey: ['period', tid, kind, inicio],
    queryFn: () => api.get<PeriodReport>(`/api/t/${tid}/reports/period?kind=${kind}${inicio ? `&inicio=${inicio}` : ''}`),
    placeholderData: keepPreviousData,
  });
  const p = q.data;
  if (q.error) return <ErrorState message={errMsg(q.error)} onRetry={() => q.refetch()} />;
  if (!p) return <Loading rows={3} height={80} />;

  return (
    <div style={{ opacity: q.isPlaceholderData ? 0.6 : 1, transition: 'opacity .2s' }}>
      <div className="row gap-8" style={{ marginBottom: 14, maxWidth: 560 }}>
        <button className="icon-btn lg" onClick={() => onNavigate(p.period.prev)} aria-label="Período anterior">←</button>
        <div className="flex1" style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 15, fontWeight: 600, letterSpacing: '-.01em' }}>{p.period.label}</div>
          <div className="mono" style={{ fontSize: 11, color: 'var(--text-2)', marginTop: 2 }}>{p.period.kindLabel}{p.isCurrent ? ' · em andamento' : ''}</div>
        </div>
        <button className="icon-btn lg" onClick={() => onNavigate(p.period.next)} disabled={!p.hasNext} style={{ color: p.hasNext ? 'var(--text)' : 'var(--faint)' }} aria-label="Próximo período">→</button>
      </div>

      <div className="grid-stats" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 130px), 1fr))', marginBottom: 14 }}>
        <Stat small value={p.metrics.visitas} label="Visitas" />
        <Stat small value={p.metrics.fotos} label="Fotos" />
        <Stat small value={p.metrics.novas} label="Pendências novas" color="var(--nc-text)" />
        <Stat small value={p.metrics.resolvidas} label="Resolvidas" color="var(--resolved)" />
        {p.metrics.notaMedia !== null && (
          <Stat small value={p.metrics.notaMedia} label={`Nota média · ${classificar(p.metrics.notaMedia)}`} color={CLASSE_COR[classificar(p.metrics.notaMedia)].fg} />
        )}
      </div>

      <section className="card flush" style={{ marginBottom: 14 }}>
        {p.rows.map((r, i) => (
          <Link key={r.restaurantId} to={`/t/${tid}/relatorio/${r.lastVisitId}`} className="row gap-12" style={{ padding: '12px 0', borderBottom: i < p.rows.length - 1 ? '1px solid var(--divider-2)' : 0, color: 'inherit' }}>
            <div className="flex1">
              <div style={{ fontSize: 13.5, fontWeight: 500 }}>{r.nome}</div>
              <div className="small" style={{ marginTop: 2 }}>{plural(r.visitas, 'visita', 'visitas')} · última {fmtDayMon(r.lastDate)}</div>
            </div>
            <div className="row gap-4 none">
              <NotaBadge nota={r.nota} />
              <span className="pill new">+{r.novas}</span>
              <span className="pill res">✓{r.resolvidas}</span>
            </div>
          </Link>
        ))}
        {p.rows.length === 0 && <div className="empty plain">Nenhuma visita neste período.</div>}
      </section>

      <div className="row wrap gap-8" style={{ marginBottom: 18 }}>
        {canShareRole && p.canShare && <Button className="flex1" size="sm" style={{ minWidth: 140, height: 44 }} onClick={() => setSharing(true)}>Compartilhar</Button>}
        <a className="btn flex1" style={{ minWidth: 140 }} href={`/api/t/${tid}/reports/period/planilha?kind=${kind}&inicio=${p.period.inicio}`} download>Baixar planilha</a>
        <div className="flex1" style={{ minWidth: 140, display: 'flex' }}>
          <PdfButton
            endpoint={`/api/t/${tid}/reports/period/pdf`}
            body={{ kind, inicio: p.period.inicio }}
            printUrl={`/r/period/${kind}/${p.period.inicio}?t=${tid}&print=1`}
            label="Baixar PDF"
            size={null}
            block
          />
        </div>
      </div>

      <Subscription tid={tid} />
      <ShareSheet tid={tid} target={sharing ? { type: 'period_report', id: p.periodKey } : null} onClose={() => setSharing(false)} />
    </div>
  );
}

function Subscription({ tid }: { tid: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: ['subscription', tid], queryFn: () => api.get<{ weekly: boolean; monthly: boolean }>(`/api/t/${tid}/reports/subscription`) });
  const save = useMutation({
    mutationFn: (v: { weekly: boolean; monthly: boolean }) => api.put(`/api/t/${tid}/reports/subscription`, v),
    onMutate: (v) => qc.setQueryData(['subscription', tid], v),
    onSuccess: (_d, v) => toast(v.weekly || v.monthly ? 'Resumo por e-mail ativado' : 'Resumo por e-mail desativado'),
    onError: (e) => { toast(errMsg(e), 'err'); qc.invalidateQueries({ queryKey: ['subscription', tid] }); },
  });
  const s = q.data ?? { weekly: false, monthly: false };
  return (
    <section className="card">
      <h2 className="h2" style={{ marginBottom: 3 }}>Resumo por e-mail</h2>
      <div className="small" style={{ marginBottom: 10, lineHeight: 1.5 }}>Receba os números do período anterior às 7h (horário de Brasília).</div>
      {([['weekly', 'Toda segunda-feira', 'Resumo da semana anterior'], ['monthly', 'Todo dia 1', 'Resumo do mês anterior']] as const).map(([k, t, d]) => (
        <div key={k} className="row gap-12" style={{ padding: '10px 0', borderTop: '1px solid var(--divider-2)' }}>
          <div className="flex1">
            <div style={{ fontSize: 13.5, fontWeight: 500 }}>{t}</div>
            <div className="small">{d}</div>
          </div>
          <Switch label={t} on={s[k]} onChange={(v) => save.mutate({ ...s, [k]: v })} />
        </div>
      ))}
    </section>
  );
}
